<template>
  <div class="concluded-orders">
    <SkeletonLoader v-if="isLoading">
      <div v-for="i in 3" :key="i" class="concluded-orders__skeleton" />
    </SkeletonLoader>

    <template v-else>
      <div
        v-for="(group, groupIndex) in orders"
        :key="groupIndex"
        class="concluded-orders__group"
      >
        <Heading class="concluded-orders__group__title" :level="6">
          {{ group.date }}
        </Heading>

        <div>
          <Row
            v-for="(order, orderIndex) in group.items"
            :key="orderIndex"
            chevron
            has-interaction
            :left-title="order.title"
            :has-line="orderIndex > 0"
            data-testid="order-row"
            @click="openOrderDetails(order)"
          >
            <template #icon-left>
              <InitialAvatarIcon size="medium" :icon="order.icon" />
            </template>
            <template #top-right>
              <Money>{{ order.value.formatted }}</Money>
            </template>
            <template v-if="order.description" #bottom-left>
              <BaseText size="sm" :color="order.description.color">
                {{ order.description.label }}
              </BaseText>
            </template>
          </Row>
        </div>
      </div>
      <ButtonIconPrimary
        v-if="hasScrolled"
        class="concluded-orders__button"
        label="Ir para o início"
        icon="EA0010"
        size="large"
        @click="goToTop"
      />
      <div v-if="isScrolling" class="concluded-orders__scroll-loader">
        <LoaderIndicator size="32" />
      </div>
    </template>
    <OrderDetailsSheet
      :is-open="isOrderDetailsOpen"
      :order-item="selectedOrder"
      @close="isOrderDetailsOpen = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import {
  SkeletonLoader,
  Heading,
  Row,
  InitialAvatarIcon,
  BaseText,
  ButtonIconPrimary,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';
import { OrderDetailsSheet } from '..';

import {
  IOrderGroup,
  IOrderItem
} from '@/modules/wealth/services/portfolio-orders';

interface Props {
  isLoading?: boolean;
  concludedOrders: IOrderGroup[];
  isScrolling?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  isScrolling: false
});

const scrollTop = ref(document.documentElement.scrollTop);
const isOrderDetailsOpen = ref(false);
const selectedOrder = ref<IOrderItem | null>(null);

const orders = computed(() => {
  return props.concludedOrders;
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 1000;
});

const openOrderDetails = (order: IOrderItem) => {
  selectedOrder.value = order;
  isOrderDetailsOpen.value = true;
};

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

onMounted(async () => {
  window.addEventListener('scroll', handleScrollTop);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});
</script>

<style lang="less" scoped>
.concluded-orders {
  padding-top: @size-spacing-x800;

  &__skeleton {
    height: 72px;
    width: 100%;
    margin-bottom: @size-spacing-x400;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__group {
    margin-bottom: @size-spacing-x500;

    &__title {
      margin-bottom: @size-spacing-x400;
    }

    :deep(.row-item > div) {
      padding: @size-spacing-x450 0;
    }

    :deep(.container-right) {
      align-self: center;
    }

    :deep(.icon) {
      align-self: center;
    }
  }

  &__button {
    position: fixed;
    bottom: @size-spacing-x600;
    right: @size-spacing-x1100;
    @media only screen and (min-width: 1150px) {
      right: calc((100vw - 1008px) / 2);
    }
  }

  &__scroll-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    width: 100%;
  }
}
</style>
